import React, { useState, useEffect } from "react";
import { Box, Paper, Typography, Tabs, Tab, Grid, Divider, Button, IconButton, ToggleButtonGroup, ToggleButton, Popover, CircularProgress, Dialog, DialogTitle, DialogContent, TextField, Radio } from "@mui/material";
import { FactCheck, MenuBook, FilterList, KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import HeaderBar from "../HeaderBar";
import WORKslogans from "../WORKslogans";
import BackArrow from "../BackArrow";
import { containerStyle, imgContainer, imgStyle } from "../formStyle";
import { headingBlue, pureWhite, mediumGrey, green, orange, maroon, red } from "../../assets/colors";
import CustomDropDown from "../CustomDropDown";
import LearningMaterialsDialog from "./Classes/LearningMaterialsDialog";
import AttendanceDialog from "./Classes/AttendanceDialog";
import TakeAssessmentDialog from "./Batch/TakeAssessmentDialog";
import HttpService from "../../backend/requestApis";
import { useDispatch } from "react-redux";
import { showSnackBar } from "../CustomComponents/CustomComponentsSlice";
import { fetchUserTrainingClasses, fetchUserTrainingBatches, fetchCoordinatorTrainingClasses, fetchCoordinatorTrainingBatches, updateTrainingClassStatus, fetchCoordinatorProgramQuestions, fetchCoordinatorTrainingPrograms, createProgramQuestion } from "../../backend/backendApis";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import Loader from "../Loader";

function TabPanel(props) {
	const { children, value, index, ...other } = props;

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			{...other}
		>
			{value === index && (
				<Box style={{ padding: '0.5rem 0rem' }}>
					{children}
				</Box>
			)}
		</div>
	);
}

const TrainingDashboard = () => {
	const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
	const [tabValue, setTabValue] = useState(0);
	const [trainingView, setTrainingView] = useState('learning');
	const [classesData, setClassesData] = useState([]);
	const [isLoadingClasses, setIsLoadingClasses] = useState(false);
	const [batchesData, setBatchesData] = useState([]);
	const [isLoadingBatches, setIsLoadingBatches] = useState(false);
	const [questionsData, setQuestionsData] = useState([]);
	const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
	const [filterAnchorEl, setFilterAnchorEl] = useState(null);
	const navigate = useNavigate();
	const dispatch = useDispatch();

	// Dialog states
	const [materialsDialogStatus, setMaterialsDialogStatus] = useState(false);
	const [attendanceDialogStatus, setAttendanceDialogStatus] = useState(false);
	const [selectedClassId, setSelectedClassId] = useState(null);
	const [selectedClassDetails, setSelectedClassDetails] = useState(null);
	const [selectedClassIsCoordinator, setSelectedClassIsCoordinator] = useState(false);
	const [takeAssessmentDialogStatus, setTakeAssessmentDialogStatus] = useState(false);
	const [selectedAssessmentBatch, setSelectedAssessmentBatch] = useState(null);
	const [isAddingQuestion, setIsAddingQuestion] = useState(false);
	const [questionsPage, setQuestionsPage] = useState(1);
	const [questionsOffset, setQuestionsOffset] = useState(10);
	const [questionsTotalRecords, setQuestionsTotalRecords] = useState(0);
	const [questionsTotalPages, setQuestionsTotalPages] = useState(1);
	
	// Add Question Form States
	const [newQuestionText, setNewQuestionText] = useState("");
	const [newQuestionDifficulty, setNewQuestionDifficulty] = useState("Easy");
	const [newQuestionOptions, setNewQuestionOptions] = useState(["", "", "", ""]);
	const [newQuestionCorrectIndex, setNewQuestionCorrectIndex] = useState(0);
	const [newQuestionProgram, setNewQuestionProgram] = useState("Select Program *");
	const [newQuestionProgramId, setNewQuestionProgramId] = useState("");
	const [programsList, setProgramsList] = useState([]);
	const [isSavingQuestion, setIsSavingQuestion] = useState(false);
	const [duplicateWarningDialog, setDuplicateWarningDialog] = useState(false);
	const [duplicateQuestionData, setDuplicateQuestionData] = useState(null);
	const [newQuestionTextError, setNewQuestionTextError] = useState("");
	const [newQuestionProgramError, setNewQuestionProgramError] = useState("");
	const [newQuestionOptionsError, setNewQuestionOptionsError] = useState("");

	// Filters
	const [classStatus, setClassStatus] = useState("All");
	const [questionProgramFilter, setQuestionProgramFilter] = useState("All");
	
	const [statusConfirmDialog, setStatusConfirmDialog] = useState(false);
	const [statusToUpdate, setStatusToUpdate] = useState({ classId: null, newStatus: null });

	useEffect(() => {
		const handleResize = () => setIsMobile(window.innerWidth <= 768);
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	const fetchUserClasses = () => {
		setIsLoadingClasses(true);
		let options = {
			url: trainingView === 'coordination' ? fetchCoordinatorTrainingClasses : fetchUserTrainingClasses,
			method: 'GET',
			headers: {
				Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null
			}
		};
		HttpService(options)
			.then(resp => {
				const data = resp?.data?.data || resp?.data || [];
				setClassesData(data.map(c => {
					let attStatus =  c.attendance || '';
					if (Array.isArray(attStatus)) {
						const currentUserContact = localStorage.getItem('contact');
						const userRecord = attStatus.find(a => 
							(currentUserContact && (String(a.contact) === String(currentUserContact) || String(a.contactNumber) === String(currentUserContact)))
						);
						attStatus = userRecord ? (userRecord.status || userRecord.attendanceStatus) : '';
					}
					return {
						...c,
						id: c._id || c.id,
						title: c.programName || c.title || 'Unknown Program',
						batch: c.batchName || c.batch || 'Unknown Batch',
						role: c.role || (trainingView === 'coordination' ? 'Coordinator' : 'Member'),
						status: c.status || 'Scheduled',
						attendanceStatus: attStatus
					};
				}));
				setIsLoadingClasses(false);
			})
			.catch(err => {
				if (err?.status === 403 || err?.response?.status === 403 || err?.response?.status === 401) {
					localStorage.clear();
					navigate('/signUp');
				} else {
					console.error("Error fetching user classes: ", err);
					dispatch(showSnackBar({ message: 'Error fetching classes. Please try again.', severity: 'error' }));
					setIsLoadingClasses(false);
				}
			});
	};

	const fetchUserBatches = () => {
		setIsLoadingBatches(true);
		let options = {
			url: trainingView === 'coordination' ? fetchCoordinatorTrainingBatches : fetchUserTrainingBatches,
			method: 'GET',
			headers: {
				Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null
			}
		};
		HttpService(options)
			.then(resp => {
				const data = resp?.data?.data || resp?.data || [];
				setBatchesData(data.map(b => ({
					...b,
					id: b._id || b.id,
					name: b.batchName || b.name || 'Unknown Batch',
					startDate: b.startDate ? moment(b.startDate).utcOffset('+05:30').format('DD/MM/YYYY') : '-',
					endDate: b.endDate ? moment(b.endDate).utcOffset('+05:30').format('DD/MM/YYYY') : '-',
					role: b.role || (trainingView === 'coordination' ? 'Coordinator' : 'Member'),
					status: b.status || 'Scheduled'
				})));
				setIsLoadingBatches(false);
			})
			.catch(err => {
				if (err?.status === 403 || err?.response?.status === 403 || err?.response?.status === 401) {
					localStorage.clear();
					navigate('/signUp');
				} else {
					console.error("Error fetching user batches: ", err);
					dispatch(showSnackBar({ message: 'Error fetching batches. Please try again.', severity: 'error' }));
					setIsLoadingBatches(false);
				}
			});
	};

	const fetchCoordinatorQuestions = () => {
		setIsLoadingQuestions(true);
		let options = {
			url: fetchCoordinatorProgramQuestions, 
			method: 'GET',
			params: {
				page: questionsPage,
				offset: questionsOffset,
				programId: questionProgramFilter !== 'All' ? programsList.find(p => p.name === questionProgramFilter)?.value : undefined
			},
			headers: {
				Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null
			}
		};
		HttpService(options)
			.then(resp => {
				const responseData = resp?.data?.data || resp?.data || {};
				const data = responseData.records || (Array.isArray(responseData) ? responseData : (responseData.data || []));
				setQuestionsData(Array.isArray(data) ? data : []);
				setQuestionsTotalRecords(responseData.totalRecords || 0);
				setQuestionsTotalPages(responseData.totalPages || 1);
				setIsLoadingQuestions(false);
			})
			.catch(err => {
				console.error("Error fetching coordinator questions: ", err);
				setIsLoadingQuestions(false);
			});
	};

	// Fetch programs for the "Select Program" dropdown and filter
	useEffect(() => {
		if (trainingView === 'coordination' && programsList.length === 0) {
			let options = {
				url: fetchCoordinatorTrainingPrograms,
				method: 'GET',
				headers: { Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null }
			};
			HttpService(options).then(resp => {
				const data = resp?.data?.data || resp?.data || [];
				setProgramsList(data.map(p => ({
					name: p.programName || p.name || 'Unknown',
					value: p._id || p.programId || p.id || p.uniqueId
				})));
			}).catch(err => console.error("Error fetching programs: ", err));
		}
	}, [trainingView, programsList.length]);

	const handleOptionChange = (text, index) => {
		const updatedOptions = [...newQuestionOptions];
		updatedOptions[index] = text;
		setNewQuestionOptions(updatedOptions);
	};

	const handleSaveQuestion = (forceSave = false) => {
		let isValid = true;

		if (!newQuestionText || !String(newQuestionText).trim()) {
			setNewQuestionTextError("Question text is required");
			isValid = false;
		} else {
			setNewQuestionTextError("");
		}
		if (newQuestionProgram === "Select Program *" || !newQuestionProgramId) {
			setNewQuestionProgramError("Program is required");
			isValid = false;
		} else {
			setNewQuestionProgramError("");
		}
		if (!Array.isArray(newQuestionOptions) || newQuestionOptions.some(opt => !opt || !String(opt).trim())) {
			setNewQuestionOptionsError("All options must be filled");
			isValid = false;
		} else {
			setNewQuestionOptionsError("");
		}
		
		if (!isValid) return;

		setIsSavingQuestion(true);
		let options = {
			url: createProgramQuestion,
			method: 'POST',
			payload: {
				programId: newQuestionProgramId,
				questionText: newQuestionText,
				difficulty: newQuestionDifficulty.toLowerCase(),
				options: newQuestionOptions,
				correctAnswer: newQuestionOptions[newQuestionCorrectIndex],
				forceSave: forceSave === true
			},
			headers: { headers: {Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null }}
		};

		HttpService(options).then(resp => {
			dispatch(showSnackBar({ message: 'Question added successfully!', severity: 'success' }));
			setIsAddingQuestion(false);
			setNewQuestionText("");
			setNewQuestionDifficulty("Easy");
			setNewQuestionOptions(["", "", "", ""]);
			setNewQuestionCorrectIndex(0);
			setNewQuestionProgram("Select Program *");
			setNewQuestionProgramId("");
			setNewQuestionTextError("");
			setNewQuestionProgramError("");
			setNewQuestionOptionsError("");
			if (questionsPage === 1) {
				fetchCoordinatorQuestions(); // Refresh list immediately
			} else {
				setQuestionsPage(1);
			}
			setIsSavingQuestion(false);
			setDuplicateWarningDialog(false);
			setDuplicateQuestionData(null);
		}).catch(err => {
			const status = err?.response?.status || err?.status;
			const errorResponse = err?.response?.data || err?.data || err || {};
			const data = errorResponse?.data || errorResponse;

			if (status === 406) {
				const similarity = data?.similarity || errorResponse?.similarity || 0;
				const duplicateQuestion = data?.duplicateQuestion

				setDuplicateQuestionData({
					similarity: similarity,
					similarQuestion: duplicateQuestion?.questionText,
					options: Array.isArray(duplicateQuestion?.options) ? duplicateQuestion.options : null,
					correctAnswer: duplicateQuestion?.correctAnswer,
					difficulty: duplicateQuestion?.difficulty
				});
				setDuplicateWarningDialog(true);
				setIsSavingQuestion(false);
				return;
			}

			console.error("Error adding question: ", err);
			dispatch(showSnackBar({ message: 'Error adding question.', severity: 'error' }));
			setIsSavingQuestion(false);
		});
	};

	useEffect(() => {
		if (tabValue === 0) {
			fetchUserClasses();
		} else if (tabValue === 1) {
			fetchUserBatches();
		} else if (tabValue === 2 && trainingView === 'coordination') { // Ensure this only runs for coordination view
			fetchCoordinatorQuestions();
		}
	}, [tabValue, trainingView, questionsPage, questionsOffset, questionProgramFilter]);

	const handleTabChange = (event, newValue) => {
		setTabValue(newValue);
	};

	const openStatusConfirmDialog = (classId, newStatus) => {
		setStatusToUpdate({ classId, newStatus });
		setStatusConfirmDialog(true);
	};

	const confirmStatusChange = () => {
		if (statusToUpdate.classId && statusToUpdate.newStatus) {
			handleStatusChange(statusToUpdate.classId, statusToUpdate.newStatus);
		}
		setStatusConfirmDialog(false);
	};

	const openMaterialsDialog = (classId, role) => {
		setSelectedClassId(classId);
		setSelectedClassIsCoordinator(role === 'Coordinator');
		setMaterialsDialogStatus(true);
	};

	const openAttendanceDialog = (classItem) => {
		setSelectedClassId(classItem.id);
		setSelectedClassDetails(classItem);
		setAttendanceDialogStatus(true);
	};

	const handleStatusChange = (classId, newStatus) => {
		let options = {
			url: updateTrainingClassStatus,
			method: 'POST',
			payload: {
				classId: classId,
				status: newStatus.toLowerCase()
			},
			headers: {
				headers: {
					Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null
				}
			}
		};

		HttpService(options)
			.then(resp => {
				dispatch(showSnackBar({ message: `Class status updated to ${newStatus}`, severity: 'success' }));
				setClassesData(prev => prev.map(c => c.id === classId ? { ...c, status: newStatus } : c));
			})
			.catch(err => {
				console.error("Error updating class status: ", err);
				dispatch(showSnackBar({ message: 'Error updating class status. Please try again.', severity: 'error' }));
			});
	};

	const filteredClasses = classesData.filter(c =>
		(classStatus === 'All' || c.status?.toLowerCase() === classStatus.toLowerCase()) &&
		(c.role?.toLowerCase() === (trainingView === 'learning' ? 'member' : 'coordinator'))
	);

	const filteredBatches = batchesData.filter(b =>
		(b.role?.toLowerCase() === (trainingView === 'learning' ? 'member' : 'coordinator'))
	);

	const filteredQuestions = questionsData.filter(q =>
		questionProgramFilter === 'All' || q.programName === questionProgramFilter || q.program === questionProgramFilter
	);

	return (
		<>
			<HeaderBar isReportForm={true} />
			<div style={{ ...containerStyle, maxWidth: "30rem" }}>
				<div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
					<WORKslogans />
				</div>
				<div style={{ ...imgContainer, padding: "0.4rem" }}>
					<img
						src={require("../../assets/images/WORK_logo.png")}
						alt="Logo"
						style={imgStyle}
					/>
				</div>
				<BackArrow />

				<Box
					style={{
						height: isMobile ? "65vh" : "58vh",
						padding: "0.5rem 0.1rem 0.8rem 0.1rem",
						display: "flex",
						flexDirection: "column",
					}}
				>
					<Paper style={{ padding: "0rem 0rem 0.2rem 0rem", marginTop: "0.5rem", flexGrow: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
						<Box style={{ borderRadius: "0.3rem 0.3rem 0rem 0rem", width: "100%", backgroundColor: headingBlue, color: pureWhite }}>
							<Typography style={{ padding: "0.4rem 0.5rem 0.4rem 0.5rem" }}>
								<strong> Training Portal </strong>
							</Typography>
						</Box>

						<Box style={{ display: 'flex', justifyContent: 'center', marginTop: '0.8rem', marginBottom: '0.2rem' }}>
							<ToggleButtonGroup
								color="primary"
								value={trainingView}
								exclusive
								onChange={(e, val) => { 
									if(val) {
										setTrainingView(val); 
										if (val === 'learning' && tabValue === 2) setTabValue(0); // Reset tab if switching from 'Programs/Questions' to 'My Learning'
									}
								}}
								sx={{
									display: "flex",
									"& .MuiToggleButton-root": {
										fontSize: "0.8rem",
										padding: "4px 16px",
										minWidth: "100px",
										color: headingBlue,
										backgroundColor: pureWhite,
										borderColor: headingBlue,
										"&.Mui-selected": {
											backgroundColor: headingBlue,
											color: "#fff",
											"&:hover": {
												backgroundColor: headingBlue,
											}
										}
									},
								}}
							>
								<ToggleButton value="learning">My Learning</ToggleButton>
								<ToggleButton value="coordination">Coordination</ToggleButton>
							</ToggleButtonGroup>
						</Box>

						<Box sx={{ borderBottom: 1, borderColor: 'divider', px: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
							<Tabs 
								value={tabValue} 
								onChange={handleTabChange} 
								TabIndicatorProps={{ style: { backgroundColor: headingBlue } }}
								sx={{ 
									minHeight: '3rem',
									'& .MuiTab-root': { 
										fontSize: '0.9rem', 
										textTransform: 'none', 
										fontWeight: 600, 
										color: '#888', 
										minHeight: '3rem',
									},
									'& .Mui-selected': { color: `${headingBlue} !important` }
								}}
							>
								<Tab label="Classes" />
								<Tab label="Batches" />
								{trainingView === 'coordination' && <Tab label="Questions" />} {/* Changed label */}
							</Tabs>
							{(tabValue === 0 || (tabValue === 2 && trainingView === 'coordination')) && (
								<IconButton onClick={(e) => setFilterAnchorEl(e.currentTarget)} size="small" style={{ color: headingBlue }}>
									<FilterList fontSize="small" />
								</IconButton>
							)}
						</Box>

						<Box 
							sx={{ 
								padding: "0.5rem 1rem", 
								flexGrow: 1, 
								overflow: "auto",
								"&::-webkit-scrollbar": { display: "none" },
								scrollbarWidth: "none" 
							}}
						>
							<TabPanel value={tabValue} index={0}>
								<Box style={{ marginTop: '1rem' }}>
									{isLoadingClasses ? (
										<Box style={{ padding: "2rem 0", display: "flex", justifyContent: "center", alignItems: "center" }}>
											{/* <CircularProgress size={30} style={{ color: headingBlue }} /> */}
											<Loader/>
										</Box>
									) : filteredClasses.length > 0 ? (
										filteredClasses.map(c => (
											<Box key={c.id} style={{ padding: '0.8rem', border: `1px solid ${mediumGrey}`, borderRadius: '0.3rem', marginBottom: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
												<Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
													<Box>
														<Typography style={{ fontWeight: 600, color: headingBlue }}>{c.title}</Typography>
														<Typography style={{ fontSize: '0.8rem', color: '#666' }}>
															{c.classDate ? moment(c.classDate).utcOffset('+05:30').format('DD/MM/YYYY | hh:mm A') : `${c.date} | ${c.time}`} | {c.batch}
														</Typography>
														{trainingView === 'learning' && (
															<Typography style={{ fontSize: '0.8rem', color: '#666' }}>
																Coordinator: {c.coordinatorName || c.volunteerName || '-'}
															</Typography>
														)}
													</Box>
													<Typography style={{ fontSize: '0.8rem', fontWeight: 600, color: c.status?.toLowerCase() === 'completed' ? green : c.status?.toLowerCase() === 'ongoing' ? orange : c.status?.toLowerCase() === 'cancelled' ? red : '#666', textTransform: 'capitalize' }}>
														{c.status}
													</Typography>
												</Box>
												<Divider style={{ margin: '0.2rem 0' }} />
												<Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
													<Box style={{ display: 'flex', gap: '0.5rem' }}>
														{trainingView === 'coordination' && c.status?.toLowerCase() === 'scheduled' && (
															<>
																<Button size="small" variant="outlined" style={{ fontSize: '0.7rem', color: orange, borderColor: orange, textTransform: 'capitalize' }} onClick={() => openStatusConfirmDialog(c.id, 'Ongoing')}>Start Class</Button>
																<Button size="small" variant="outlined" style={{ fontSize: '0.7rem', color: 'red', borderColor: 'red', textTransform: 'capitalize' }} onClick={() => openStatusConfirmDialog(c.id, 'Cancelled')}>Cancel Class</Button>
															</>
														)}
														{trainingView === 'coordination' && c.status?.toLowerCase() === 'ongoing' && (
															<Button size="small" variant="outlined" style={{ fontSize: '0.7rem', color: green, borderColor: green, textTransform: 'capitalize' }} onClick={() => openStatusConfirmDialog(c.id, 'Completed')}>Mark Completed</Button>
														)}
													</Box>
													<Box style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
														{trainingView === 'coordination' && (
																<IconButton size="small" style={{ color: headingBlue }} onClick={() => openAttendanceDialog(c)}>
																	<FactCheck fontSize="small" />
																</IconButton>
														)}
														{trainingView === 'learning' && c.status?.toLowerCase() === 'completed' && c.attendanceStatus && (
															<Typography style={{ fontSize: '0.85rem', fontWeight: 600, color: String(c.attendanceStatus).toLowerCase() === 'present' ? green : String(c.attendanceStatus).toLowerCase() === 'absent' ? red : '#666', marginRight: '0.2rem', textTransform: 'capitalize' }}>
																{String(c.attendanceStatus)}
															</Typography>
														)}
														<IconButton size="small" style={{ color: headingBlue }} onClick={() => openMaterialsDialog(c.id, c.role)}>
															<MenuBook fontSize="small" />
														</IconButton>
													</Box>
												</Box>
											</Box>
										))
									) : (
										<Box style={{ padding: "2rem 0", display: "flex", justifyContent: "center", alignItems: "center" }}>
											<Typography style={{ color: mediumGrey, fontSize: '0.9rem' }}>
												No classes found.
											</Typography>
										</Box>
									)}
								</Box>
							</TabPanel>

							<TabPanel value={tabValue} index={1}>
								<Box style={{ marginTop: '1rem' }}>
									{isLoadingBatches ? (
										<Box style={{ padding: "2rem 0", display: "flex", justifyContent: "center", alignItems: "center" }}>
											{/* <CircularProgress size={30} style={{ color: headingBlue }} /> */}
											<Loader />
										</Box>
									) : filteredBatches.length > 0 ? (
										filteredBatches.map(b => (
											<Box key={b.id} style={{ padding: '0.8rem', border: `1px solid ${mediumGrey}`, borderRadius: '0.3rem', marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
												<Box>
													<Typography style={{ fontWeight: 600, color: headingBlue }}>{b.name}</Typography>
													<Typography style={{ fontSize: '0.8rem', color: '#666' }}>{b.startDate} to {b.endDate}</Typography>
													{trainingView === 'learning' && (
														<Typography style={{ fontSize: '0.8rem', color: '#666' }}>
															Coordinator: {b.coordinatorName || b.volunteerName || '-'}
														</Typography>
													)}
												</Box>
												<Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.4rem' }}>
													<Typography style={{ fontSize: '0.8rem', fontWeight: 600, color: b.status?.toLowerCase() === 'completed' ? green : b.status?.toLowerCase() === 'ongoing' ? orange : b.status?.toLowerCase() === 'cancelled' ? red : b.status?.toLowerCase() === 'scheduled' ? maroon : '#666', textTransform: 'capitalize' }}>{b.status}</Typography>
													{trainingView === 'learning' && b.status?.toLowerCase() !== 'scheduled' && (
														<Button 
															size="small" 
															variant="outlined" 
															style={{ color: headingBlue, borderColor: headingBlue, textTransform: 'capitalize', padding: '0.1rem 0.5rem', fontSize: '0.75rem' }}
															onClick={() => {
																setSelectedAssessmentBatch(b);
																setTakeAssessmentDialogStatus(true);
															}}
														>
															Take Assessment
														</Button>
													)}
												</Box>
											</Box>
										))
									) : (
										<Box style={{ padding: "2rem 0", display: "flex", justifyContent: "center", alignItems: "center" }}>
											<Typography style={{ color: mediumGrey, fontSize: '0.9rem' }}>
												No batches found.
											</Typography>
										</Box>
									)}
								</Box>
							</TabPanel>

							{trainingView === 'coordination' && (
								<TabPanel value={tabValue} index={2}>
									<Box style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.8rem' }}>
										{!isAddingQuestion && (
											<Button variant="contained" style={{ backgroundColor: headingBlue, color: 'white', padding: '0.3rem 1rem', textTransform: 'capitalize' }} onClick={() => setIsAddingQuestion(true)}>
												Add New Question
											</Button>
										)}
									</Box>
									<Box> {/* New TabPanel content for Questions */}
										{isAddingQuestion && (
											<Box style={{ padding: '1rem', border: `1px solid ${headingBlue}`, borderRadius: '0.3rem', marginBottom: '1rem', backgroundColor: '#f4f9ff', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
												{isSavingQuestion && <Loader />}
												<Typography style={{ fontWeight: 600, color: headingBlue, fontSize: '1rem' }}>Create New Question</Typography>
												
												<Box style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
													<Box style={{ width: '100%' }}>
														<TextField 
															fullWidth 
															size="small" 
															label="Question Text *" 
															variant="outlined" 
															placeholder="Enter the question here..."
															sx={{ backgroundColor: 'white' }}
															value={newQuestionText}
															onChange={(e) => { setNewQuestionText(e.target.value); setNewQuestionTextError(""); }}
														/>
														<Typography style={{ color: red, fontSize: '0.75rem', marginTop: '0.2rem', marginLeft: '0.4rem', fontWeight: 500 }}>{newQuestionTextError}</Typography>
													</Box>
													<Box style={{ width: '12rem' }}>
														<CustomDropDown
															placeHolder="Difficulty *"
															list={[{name: 'Easy'}, {name: 'Medium'}, {name: 'Hard'}]}
															value={newQuestionDifficulty}
															setFunc={(val) => setNewQuestionDifficulty(typeof val === 'object' ? val.name : val)}
															width="100%"
															regular={true}
														/>
													</Box>
												</Box>

												<Box style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', backgroundColor: 'white', padding: '0.8rem', borderRadius: '0.3rem', border: `1px solid ${mediumGrey}` }}>
													<Typography style={{ fontSize: '0.85rem', color: headingBlue, fontWeight: 600, marginBottom: '0.3rem' }}>Options (Select the correct answer)</Typography>
													{[0, 1, 2, 3].map((idx) => (
														<Box key={idx} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
															<Radio 
																checked={newQuestionCorrectIndex === idx} 
																onChange={() => setNewQuestionCorrectIndex(idx)}
																size="small" 
																style={{ color: newQuestionCorrectIndex === idx ? green : mediumGrey, padding: '0.2rem' }} 
															/>
															<TextField 
																fullWidth 
																size="small" 
																placeholder={`Option ${idx + 1}`} 
																variant="outlined" 
																value={newQuestionOptions[idx]}
																onChange={(e) => { handleOptionChange(e.target.value, idx); setNewQuestionOptionsError(""); }}
															/>
														</Box>
													))}
													<Typography style={{ color: red, fontSize: '0.75rem', marginTop: '0.1rem', marginLeft: '0.4rem', fontWeight: 500 }}>{newQuestionOptionsError}</Typography>
												</Box>

												<Box style={{ width: '50%' }}>
													<CustomDropDown
														placeHolder="Select Program *"
														list={programsList}
														value={newQuestionProgram}
														setFunc={(val) => {
															setNewQuestionProgram(typeof val === 'object' ? val.name : val);
															setNewQuestionProgramId(typeof val === 'object' ? val.value : programsList.find(p => p.name === val)?.value);
															setNewQuestionProgramError("");
														}}
														width="100%"
														regular={true}
													/>
													<Typography style={{ color: red, fontSize: '0.75rem', marginTop: '0.2rem', marginLeft: '0.4rem', fontWeight: 500 }}>{newQuestionProgramError}</Typography>
												</Box>

												<Box style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.5rem' }}>
													<Button size="small" variant="outlined" style={{ color: red, borderColor: red, textTransform: 'capitalize' }} onClick={() => {
														setIsAddingQuestion(false);
														setNewQuestionText("");
														setNewQuestionDifficulty("Easy");
														setNewQuestionOptions(["", "", "", ""]);
														setNewQuestionProgram("Select Program *");
														setNewQuestionProgramId("");
														setNewQuestionTextError("");
														setNewQuestionProgramError("");
														setNewQuestionOptionsError("");
													}}>Cancel</Button>
													<Button size="small" variant="contained" style={{ backgroundColor: headingBlue, color: 'white', textTransform: 'capitalize' }} onClick={() => handleSaveQuestion(false)} disabled={isSavingQuestion}>
														Save Question
													</Button>
												</Box>
											</Box>
										)}
										{isLoadingQuestions ? (
											<Box style={{ padding: "2rem 0", display: "flex", justifyContent: "center", alignItems: "center" }}>
												<Loader  />
											</Box>
										) : filteredQuestions.length > 0 ? (
											filteredQuestions.map((q, idx) => (
												<Box key={q._id || q.id || idx} style={{ padding: '0.8rem', border: `1px solid ${mediumGrey}`, borderRadius: '0.3rem', marginBottom: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
													<Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
														<Typography style={{ fontWeight: 600, color: headingBlue }}>{q.questionText || q.question || 'Unknown Question'}</Typography>
														<Typography style={{ fontSize: '0.75rem', fontWeight: 600, padding: '0.1rem 0.4rem', borderRadius: '4px', backgroundColor: (q.difficulty || q.difficultyLevel)?.toLowerCase() === 'hard' ? '#ffebee' : (q.difficulty || q.difficultyLevel)?.toLowerCase() === 'medium' ? '#fff3e0' : '#e8f5e9', color: (q.difficulty || q.difficultyLevel)?.toLowerCase() === 'hard' ? red : (q.difficulty || q.difficultyLevel)?.toLowerCase() === 'medium' ? orange : green, textTransform: 'capitalize' }}>
															{q.difficulty || 'N/A'}
														</Typography>
													</Box>
													
													<Box style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginTop: '0.3rem' }}>
														{Array.isArray(q.options) ? q.options.map((opt, oIdx) => {
															const optText = typeof opt === 'object' ? (opt.text || opt.optionText || opt.option) : opt;
															const isCorrect = (q.correctAnswer === optText) || (q.correctOption === optText) || opt?.isCorrect;
															return (
																<Box key={oIdx} style={{ padding: '0.4rem 0.6rem', border: `1px solid ${isCorrect ? green : mediumGrey}`, borderRadius: '0.3rem', backgroundColor: isCorrect ? '#e8f5e9' : '#fafafa', display: 'flex', justifyContent: 'space-between' }}>
																	<Typography style={{ fontSize: '0.85rem', color: isCorrect ? 'green' : 'black', fontWeight: isCorrect ? 600 : 400 }}>{optText}</Typography>
																	{isCorrect && <Typography style={{ fontSize: '0.75rem', color: 'green', fontWeight: 600 }}>Correct</Typography>}
																</Box>
															);
														}) : (
															<Typography style={{ fontSize: '0.85rem', color: '#666' }}>No options available.</Typography>
														)}
													</Box>

													<Divider style={{ margin: '0.5rem 0' }} />
													
													<Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '0.5rem' }}>
														<Typography style={{ fontSize: '0.8rem', color: '#666' }}>
															<strong style={{color: '#333'}}>Program:</strong> {q.programName || q.program || '-'}
														</Typography>
														<Typography style={{ fontSize: '0.8rem', color: '#666', textAlign: 'right' }}>
															<strong style={{color: '#333'}}>Created By:</strong> {q.createdByName || '-'} {q.createdByCN ? `(${q.createdByCN})` : ''}
														</Typography>
													</Box>
												</Box>
											))
										) : (
											<Box style={{ padding: "2rem 0", display: "flex", justifyContent: "center", alignItems: "center" }}>
												<Typography style={{ color: mediumGrey, fontSize: '0.9rem' }}>
													No questions found.
												</Typography>
											</Box>
										)}
										{(
											<Box style={{ display: 'flex', justifyContent: 'center', padding: "0.5rem 0rem", marginTop: 'auto', position: 'sticky', bottom: '-0.5rem', backgroundColor: 'white', zIndex: 10 }}>
												<Box style={{ width: "30%", display: 'flex', justifyContent: 'flex-start', alignItems: 'center', padding: "0rem 0.5rem 0rem 0.9rem" }}>
													<Typography style={{ color: maroon, fontSize: '0.9rem' }}>
														{((questionsPage - 1) * questionsOffset) + 1} - {Math.min(questionsPage * questionsOffset, questionsTotalRecords)} of {questionsTotalRecords}
													</Typography>
												</Box>
												<Box style={{ width: '40%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
													<IconButton 
														size='small'
														onClick={() => { if(questionsPage > 1) setQuestionsPage(questionsPage - 1) }}
														disabled={questionsPage === 1}
													>
														<KeyboardArrowLeft size='small' sx={{ color: questionsPage === 1 ? mediumGrey : headingBlue }} />
													</IconButton>
													<Button variant='contained' style={{ minWidth: '0rem', width: '1.5rem', height: '1.5rem', borderRadius: '50%', backgroundColor: headingBlue, color: 'white', padding: '0rem' }}>
														{questionsPage}
													</Button>
													<IconButton 
														size='small'
														onClick={() => { if(questionsPage < questionsTotalPages) setQuestionsPage(questionsPage + 1) }}
														disabled={questionsPage === questionsTotalPages || !questionsTotalPages}
													>
														<KeyboardArrowRight size='small' sx={{ color: (questionsPage === questionsTotalPages || !questionsTotalPages) ? mediumGrey : headingBlue }} />
													</IconButton>
												</Box>
												<Box style={{ width: '30%', display: 'flex', justifyContent: 'flex-end', padding: "0rem 0.6rem 0rem 0rem" }}>
													<CustomDropDown 
														key='table' 
														value={questionsOffset} 
														width="5rem" 
														setFunc={(val) => { setQuestionsOffset(typeof val === 'object' ? val.name : val); setQuestionsPage(1); }} 
														list={[{name: 10}, {name: 20}, {name: 50}]} 
														regular={true} 
													/>
												</Box>
											</Box>
										)}
									</Box>
								</TabPanel>
							)}
						</Box>
					</Paper>
				</Box>
			</div>
			<Popover
				open={Boolean(filterAnchorEl)}
				anchorEl={filterAnchorEl}
				onClose={() => setFilterAnchorEl(null)}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
			>
				<Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: '1rem', width: '16rem' }}>
					<Typography variant="subtitle2" style={{color: headingBlue, fontWeight: 'bold'}}>Filters</Typography>
					{tabValue === 0 && (
						<>
							<CustomDropDown
								regular={true}
								placeHolder="Status"
								list={[{name: 'All'}, {name: 'Scheduled'}, {name: 'Ongoing'}, {name: 'Completed'}, {name: 'Cancelled'}]}
								value={classStatus}
								setFunc={(val) => setClassStatus(typeof val === 'object' ? val.name : val)}
								width="14rem"
							/>
						</>
					)}
					{tabValue === 2 && trainingView === 'coordination' && (
						<>
							<CustomDropDown
								regular={true}
								placeHolder="Program"
								list={[{name: 'All'}, ...programsList]}
								value={questionProgramFilter}
								setFunc={(val) => { setQuestionProgramFilter(typeof val === 'object' ? val.name : val); setQuestionsPage(1); }}
								width="14rem"
							/>
						</>
					)}
					<Box style={{display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem'}}>
						<Button onClick={() => { setClassStatus('All'); setQuestionProgramFilter('All'); setQuestionsPage(1); }} size="small" style={{ color: headingBlue, border: `1px solid ${headingBlue}`, marginRight: '0.5rem', textTransform: 'capitalize' }}>Clear</Button>
						<Button onClick={() => setFilterAnchorEl(null)} size="small" style={{ backgroundColor: headingBlue, color: 'white', textTransform: 'capitalize' }}>Apply</Button>
					</Box>
				</Box>
			</Popover>
			<LearningMaterialsDialog 
				dialogStatus={materialsDialogStatus} 
				updateFunc={setMaterialsDialogStatus} 
				classId={selectedClassId} 
				isCoordinator={selectedClassIsCoordinator}
			/>
			<AttendanceDialog 
				dialogStatus={attendanceDialogStatus} 
				updateFunc={setAttendanceDialogStatus} 
				classId={selectedClassId} 
				classDetails={selectedClassDetails}
				isCoordinator={trainingView === 'coordination'}
			/>
			<Dialog maxWidth="xs" fullWidth open={statusConfirmDialog} onClose={() => setStatusConfirmDialog(false)}>
				<DialogTitle style={{ backgroundColor: headingBlue, color: 'white', fontSize: '1rem', padding: '0.4rem 0rem 0.4rem 1rem' }}>
					Confirmation
				</DialogTitle>
				<DialogContent style={{ marginTop: '1rem', textAlign: 'center' }}>
					<Typography style={{ color: maroon, padding: '0.5rem', fontSize: '0.9rem' }}>
						Are you sure you want to update the class status to '{statusToUpdate.newStatus}'?
					</Typography>
					<Box style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: '1rem' }}>
						<Button size="small" style={{ backgroundColor: red, color: 'white', padding: '0.2rem 1rem', textTransform: 'capitalize' }} onClick={() => setStatusConfirmDialog(false)}>
							Cancel
						</Button>
						<Button size="small" style={{ backgroundColor: headingBlue, color: 'white', padding: '0.2rem 1rem', textTransform: 'capitalize' }} onClick={confirmStatusChange}>
							Confirm
						</Button>
					</Box>
				</DialogContent>
			</Dialog>
			<Dialog maxWidth="sm" fullWidth open={duplicateWarningDialog} onClose={() => setDuplicateWarningDialog(false)}>
				<DialogTitle style={{ backgroundColor: headingBlue, color: 'white', fontSize: '1rem', padding: '0.4rem 0rem 0.4rem 1rem' }}>
					Duplication Warning
				</DialogTitle>
				<DialogContent style={{ marginTop: '1rem', textAlign: 'center' }}>
					{isSavingQuestion && <Loader />}
					<Typography style={{ color: '#333', marginBottom: '1rem', fontSize: '0.9rem' }}>
						A similar question already exists in this program.
					</Typography>
					{duplicateQuestionData && (
						<Box style={{ padding: '0.8rem', backgroundColor: '#f5f5f5', borderRadius: '4px', marginBottom: '1rem', textAlign: 'left' }}>
							<Typography style={{ fontSize: '0.85rem', color: '#666' }}>Similar Question ({Math.round(duplicateQuestionData.similarity * 100)}% match):</Typography>
							<Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginTop: '0.2rem' }}>
								<Typography style={{ fontSize: '0.9rem', fontWeight: 600, color: headingBlue }}>{duplicateQuestionData.similarQuestion}</Typography>
								{duplicateQuestionData.difficulty && (
									<Typography style={{ fontSize: '0.75rem', fontWeight: 600, padding: '0.1rem 0.4rem', borderRadius: '4px', backgroundColor: duplicateQuestionData.difficulty?.toLowerCase() === 'hard' ? '#ffebee' : duplicateQuestionData.difficulty?.toLowerCase() === 'medium' ? '#fff3e0' : '#e8f5e9', color: duplicateQuestionData.difficulty?.toLowerCase() === 'hard' ? red : duplicateQuestionData.difficulty?.toLowerCase() === 'medium' ? orange : green, textTransform: 'capitalize' }}>
										{duplicateQuestionData.difficulty}
									</Typography>
								)}
							</Box>
							{duplicateQuestionData.options && Array.isArray(duplicateQuestionData.options) && (
								<Box style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', marginTop: '0.5rem', marginLeft: '0.5rem' }}>
									{duplicateQuestionData.options.map((opt, idx) => {
										const optText = typeof opt === 'object' ? (opt.text || opt.optionText || opt.option) : opt;
										const isCorrect = (duplicateQuestionData.correctAnswer === optText) || opt?.isCorrect;
										return (
											<Typography key={idx} style={{ fontSize: '0.8rem', color: isCorrect ? green : '#555', fontWeight: isCorrect ? 600 : 400 }}>
												{idx + 1}. {optText} {isCorrect ? '(Correct)' : ''}
											</Typography>
										);
									})}
								</Box>
							)}
						</Box>
					)}
					<Typography style={{ color: '#333', fontSize: '0.9rem', marginBottom: '1rem' }}>
						{duplicateQuestionData?.similarity >= 0.9 
							? "This question is too similar to an existing one and cannot be saved."
							: "Do you still want to save this question?"}
					</Typography>
					<Box style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: '1rem' }}>
						<Button size="small" style={{ backgroundColor: red, color: 'white', padding: '0.2rem 1rem', textTransform: 'capitalize' }} onClick={() => setDuplicateWarningDialog(false)}>
							{duplicateQuestionData?.similarity >= 0.9 ? "Close" : "Cancel"}
						</Button>
						{(!duplicateQuestionData || duplicateQuestionData.similarity < 0.9) && (
							<Button size="small" style={{ backgroundColor: headingBlue, color: 'white', padding: '0.2rem 1rem', textTransform: 'capitalize' }} onClick={() => handleSaveQuestion(true)} disabled={isSavingQuestion}>
								Save Anyway
							</Button>
						)}
					</Box>
				</DialogContent>
			</Dialog>
			<TakeAssessmentDialog 
				dialogStatus={takeAssessmentDialogStatus}
				updateFunc={(status, shouldRefresh) => {
					setTakeAssessmentDialogStatus(status);
					if(shouldRefresh) fetchUserBatches();
				}}
				batchId={selectedAssessmentBatch?.id}
				batchName={selectedAssessmentBatch?.name}
			/>
		</>
	);
};

export default TrainingDashboard;